// Export a Catalyst chat conversation (or a single result) to a downloadable
// `.eml` file the user opens to get an editable email draft.
//
// Why .eml (not clipboard / base64-in-HTML): a MIME `multipart/related` message
// carries each chart as an inline `cid:` image part — the same mechanism normal
// received email uses — so charts render reliably in Outlook *classic* (Word
// engine) where pasted `data:` base64 images break, as well as new Outlook,
// Apple Mail, Thunderbird, etc. The `X-Unsent: 1` header makes Outlook open the
// file as a ready-to-edit draft. Everything here runs client-side.
//
// The email body is generated from the message DATA (parts), not scraped from
// the live DOM, so we fully control the inline styles mail clients require
// (classes and <style> blocks get stripped). The one thing that must go through
// real rendering is each chart → PNG, done in-browser via the shared component
// kit + SVG→canvas, behind the `renderVisualToPng` seam.
//
// Convergence: a parallel effort will add a server-side chart renderer (Catalyst
// Node backend) for scheduled emails. `renderVisualToPng(toolName, result)` is
// the seam — swap its body to POST the same `{ toolName, result }` spec and
// receive PNG base64, with no change to the .eml assembly or the UI.

import React from 'react';
import { createRoot } from 'react-dom/client';
import { renderDisplayTool, Markdown } from '@usalco/catalyst-component-kit';
import { SAVABLE_TOOL_NAMES, TOOL_SHOW_TABLE } from './constants';

// Brand palette — mirrors the kit's defaultChatKitTheme so the email matches the
// chat's look (node_modules/@usalco/catalyst-component-kit/src/theme.tsx).
const C = {
  primary: '#262A56',
  accent: '#5155D5',
  primaryLighter: '#F0F0FF',
  surface: '#FFFFFF',
  background: '#F7F8FD',
  text: '#323232',
  textSecondary: '#616161',
  border: '#CCCCCC',
  borderLight: '#F2F2F2',
  font: 'Segoe UI, Arial, Helvetica, sans-serif', // web-safe for mail clients
};

const EMAIL_WIDTH = 640;     // off-screen render + email body width (px)
const RENDER_TIMEOUT_MS = 8000;
// Syncfusion series animate from zero to final over ~1s after the <svg> first
// appears; capturing too early rasterizes a half-drawn chart. Wait past the
// default animation duration before snapshotting.
const SETTLE_MS = 1300;
const PNG_SCALE = 2;         // 2x for crisp charts on hi-dpi / zoom

// ---------------------------------------------------------------------------
// Off-screen rendering
// ---------------------------------------------------------------------------

// Mount a React element in an off-screen, white, fixed-width host attached to
// the document (Syncfusion needs a laid-out, sized container to render into).
function mountOffscreen(element, width = EMAIL_WIDTH) {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = [
    'position:fixed', 'left:-10000px', 'top:0', `width:${width}px`,
    'background:#ffffff', 'margin:0', 'padding:0', 'z-index:-1',
    'pointer-events:none', 'opacity:0',
  ].join(';');
  document.body.appendChild(host);
  const root = createRoot(host);
  root.render(element);
  return {
    host,
    cleanup() {
      try { root.unmount(); } catch { /* noop */ }
      try { host.remove(); } catch { /* noop */ }
    },
  };
}

// Resolve once the host contains a rendered (non-empty, sized) <svg>. Charts and
// maps render asynchronously, so we poll and then let it settle before capture.
function waitForSvg(host, timeout = RENDER_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      let best = null;
      let bestArea = 0;
      host.querySelectorAll('svg').forEach((svg) => {
        const r = svg.getBoundingClientRect();
        const area = r.width * r.height;
        if (svg.childNodes.length > 0 && area > bestArea) {
          best = svg;
          bestArea = area;
        }
      });
      if (best && bestArea > 100) {
        setTimeout(() => resolve(best), SETTLE_MS);
        return;
      }
      if (Date.now() - start > timeout) {
        reject(new Error('Timed out rendering visualization.'));
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

// Rasterize an <svg> node to a white-background PNG. Returns raw base64 (no data:
// prefix) plus the logical (css-pixel) dimensions. Raw base64 matches the future
// server-renderer contract.
function svgToPng(svg) {
  return new Promise((resolve, reject) => {
    const rect = svg.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    const clone = svg.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
    clone.setAttribute('width', String(width));
    clone.setAttribute('height', String(height));
    const xml = new XMLSerializer().serializeToString(clone);
    const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = width * PNG_SCALE;
        canvas.height = height * PNG_SCALE;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.scale(PNG_SCALE, PNG_SCALE);
        ctx.drawImage(img, 0, 0, width, height);
        const base64 = canvas.toDataURL('image/png').split(',')[1];
        resolve({ base64, width, height });
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Failed to rasterize visualization.'));
    img.src = svgUrl;
  });
}

// SEAM: display-tool spec -> PNG. In-browser implementation today; swap the body
// to POST { toolName, result } to the Catalyst Node renderer when it exists.
export async function renderVisualToPng(toolName, result) {
  const element = renderDisplayTool(toolName, result);
  if (!element) return null;
  const wrapped = React.createElement(
    'div',
    { style: { width: '100%', background: '#ffffff', padding: '8px' } },
    element,
  );
  const mounted = mountOffscreen(wrapped);
  try {
    const svg = await waitForSvg(mounted.host);
    return await svgToPng(svg);
  } finally {
    mounted.cleanup();
  }
}

// Render markdown to an HTML string using the exact kit pipeline (react-markdown
// + remark-gfm), then inline per-tag styles so it survives mail clients.
function markdownToInlineHtml(md) {
  return new Promise((resolve) => {
    const mounted = mountOffscreen(
      React.createElement('div', { 'data-md-root': '1' }, React.createElement(Markdown, { text: md })),
    );
    // createRoot renders asynchronously; Markdown is synchronous content, so two
    // frames is enough to read the committed DOM.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const node = mounted.host.firstChild;
      const html = node ? inlineMarkdownStyles(node) : '';
      mounted.cleanup();
      resolve(html);
    }));
  });
}

// react-markdown emits semantic tags; the kit maps <p> to <div>. Map each known
// tag to an email-safe inline style.
const MD_TAG_STYLES = {
  H1: 'font-size:20px;font-weight:700;margin:0 0 8px;line-height:1.3;',
  H2: 'font-size:17px;font-weight:700;margin:12px 0 8px;line-height:1.3;',
  H3: 'font-size:15px;font-weight:700;margin:10px 0 6px;line-height:1.3;',
  H4: 'font-size:14px;font-weight:700;margin:10px 0 6px;line-height:1.3;',
  P: 'margin:0 0 10px;line-height:1.5;',
  DIV: 'margin:0 0 10px;line-height:1.5;',
  UL: 'margin:0 0 10px;padding-left:22px;',
  OL: 'margin:0 0 10px;padding-left:22px;',
  LI: 'margin:0 0 4px;line-height:1.5;',
  STRONG: 'font-weight:700;',
  B: 'font-weight:700;',
  EM: 'font-style:italic;',
  A: `color:${C.accent};text-decoration:underline;`,
  CODE: 'font-family:Consolas,Menlo,monospace;background:#f3f3f7;padding:1px 4px;border-radius:3px;font-size:13px;',
  PRE: 'background:#f3f3f7;padding:10px;border-radius:6px;overflow:auto;margin:0 0 10px;font-family:Consolas,Menlo,monospace;font-size:13px;',
  BLOCKQUOTE: `margin:0 0 10px;padding:4px 0 4px 12px;border-left:3px solid ${C.border};color:${C.textSecondary};`,
  HR: `border:0;border-top:1px solid ${C.borderLight};margin:12px 0;`,
  TABLE: 'border-collapse:collapse;width:100%;margin:0 0 10px;font-size:13px;',
  THEAD: '',
  TBODY: '',
  TR: '',
  TH: `border:1px solid ${C.border};padding:6px 8px;background:${C.primaryLighter};text-align:left;font-weight:700;`,
  TD: `border:1px solid ${C.borderLight};padding:6px 8px;`,
};

function inlineMarkdownStyles(rootNode) {
  rootNode.querySelectorAll('*').forEach((el) => {
    const style = MD_TAG_STYLES[el.tagName];
    if (style != null) el.setAttribute('style', style);
  });
  return rootNode.innerHTML;
}

// ---------------------------------------------------------------------------
// Table (show_table) -> inline-styled HTML. Mirrors the kit's TableDisplay
// formatting so the email matches the chat.
// ---------------------------------------------------------------------------

function formatCell(value, type) {
  if (value === null || value === undefined) return '—';
  switch (type) {
    case 'currency':
      return typeof value === 'number'
        ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
        : String(value);
    case 'percent':
      return typeof value === 'number' ? `${value.toFixed(1)}%` : String(value);
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : String(value);
    case 'date':
      try { return new Date(String(value)).toLocaleDateString(); } catch { return String(value); }
    default:
      return String(value);
  }
}

function defaultAlign(type) {
  return type === 'number' || type === 'currency' || type === 'percent' ? 'right' : 'left';
}

function tableToInlineHtml(result) {
  const columns = Array.isArray(result?.columns) ? result.columns : [];
  const rows = Array.isArray(result?.rows) ? result.rows : [];
  const headerBg = result?.headerColor || C.primaryLighter;
  const headerFg = result?.headerTextColor || C.text;
  const rowBg = result?.rowColor || C.surface;
  const altRowBg = result?.altRowColor || C.background;

  const head = columns.map((col) => {
    const align = col.align || defaultAlign(col.type);
    return `<th style="padding:7px 12px;text-align:${align};font-weight:600;color:${headerFg};`
      + `border-bottom:1px solid ${C.border};white-space:nowrap;font-size:11.5px;">`
      + `${escapeHtml(col.label ?? col.key)}</th>`;
  }).join('');

  const body = rows.map((row, ri) => {
    const bg = ri % 2 === 1 ? altRowBg : rowBg;
    const cells = columns.map((col) => {
      const align = col.align || defaultAlign(col.type);
      const border = ri < rows.length - 1 ? `border-bottom:1px solid ${C.borderLight};` : '';
      return `<td style="padding:6px 12px;text-align:${align};color:${C.text};${border}">`
        + `${escapeHtml(formatCell(row[col.key], col.type))}</td>`;
    }).join('');
    return `<tr style="background:${bg};">${cells}</tr>`;
  }).join('');

  const summary = result?.summary
    ? `<div style="padding:7px 14px;font-size:11.5px;color:${C.textSecondary};border-top:1px solid ${C.borderLight};">${escapeHtml(result.summary)}</div>`
    : '';

  return `<div style="border:1px solid ${C.borderLight};border-radius:8px;overflow:hidden;margin:6px 0 12px;">`
    + `<table style="width:100%;border-collapse:collapse;font-size:12.5px;">`
    + `<thead><tr style="background:${headerBg};">${head}</tr></thead>`
    + `<tbody>${body}</tbody></table>${summary}</div>`;
}

// ---------------------------------------------------------------------------
// Block builders
// ---------------------------------------------------------------------------

function userBlock(text) {
  return `<div style="margin:16px 0 8px;">`
    + `<div style="font-size:11px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;color:${C.accent};margin-bottom:4px;">You asked</div>`
    + `<div style="background:${C.primaryLighter};border-radius:8px;padding:10px 12px;font-size:14px;line-height:1.5;color:${C.text};white-space:pre-wrap;">${escapeHtml(text)}</div>`
    + `</div>`;
}

function assistantContentBlock(innerHtml) {
  return `<div style="border-left:3px solid rgba(81,85,213,0.35);padding:4px 0 4px 12px;margin:0 0 8px;font-size:14px;color:${C.text};">${innerHtml}</div>`;
}

function titleHtml(title) {
  if (!title) return '';
  return `<div style="font-size:13px;font-weight:600;color:${C.text};margin:0 0 6px;">${escapeHtml(title)}</div>`;
}

function imageHtml(cid, width, title) {
  const w = Math.min(width || EMAIL_WIDTH, EMAIL_WIDTH);
  return titleHtml(title)
    + `<img src="cid:${cid}" width="${w}" style="display:block;max-width:100%;height:auto;`
    + `border:1px solid ${C.borderLight};border-radius:6px;" alt="${escapeHtml(title || 'chart')}" />`;
}

function wrapDocument(bodyHtml) {
  return `<!doctype html><html><head><meta charset="utf-8" />`
    + `<meta name="viewport" content="width=device-width, initial-scale=1" /></head>`
    + `<body style="margin:0;padding:0;background:#ffffff;">`
    + `<div style="max-width:${EMAIL_WIDTH}px;margin:0 auto;padding:20px 16px;`
    + `font-family:${C.font};color:${C.text};font-size:14px;line-height:1.5;">`
    + `<div style="font-size:16px;font-weight:700;color:${C.primary};margin:0 0 4px;">Catalyst Data Assistant</div>`
    + `<div style="font-size:12px;color:${C.textSecondary};margin:0 0 16px;border-bottom:1px solid ${C.borderLight};padding-bottom:12px;">Shared from The Reservoir</div>`
    + bodyHtml
    + `<div style="margin-top:20px;padding-top:12px;border-top:1px solid ${C.borderLight};font-size:11px;color:${C.textSecondary};">Catalyst uses AI and can make mistakes. Please verify important responses.</div>`
    + `</div></body></html>`;
}

// ---------------------------------------------------------------------------
// Conversation -> { bodyHtml, images }
// ---------------------------------------------------------------------------

async function buildEmailBody(messages) {
  const images = []; // { cid, base64 }
  const blocks = [];
  let seq = 0;

  for (const msg of messages) {
    if (!msg || !Array.isArray(msg.parts)) continue;

    if (msg.role === 'user') {
      const text = msg.parts.filter((p) => p.type === 'text').map((p) => p.text).join('').trim();
      if (text) blocks.push(userBlock(text));
      continue;
    }

    // assistant: interleave markdown text and display-tool visuals, in emit order.
    for (const part of msg.parts) {
      if (part.type === 'text') {
        if (!part.text || !part.text.trim()) continue;
        const html = await markdownToInlineHtml(part.text); // eslint-disable-line no-await-in-loop
        if (html) blocks.push(assistantContentBlock(html));
        continue;
      }
      if (part.type === 'tool' && part.state === 'result' && SAVABLE_TOOL_NAMES.includes(part.toolName)) {
        if (part.toolName === TOOL_SHOW_TABLE) {
          blocks.push(assistantContentBlock(titleHtml(part.result?.title) + tableToInlineHtml(part.result)));
        } else {
          try {
            // eslint-disable-next-line no-await-in-loop
            const png = await renderVisualToPng(part.toolName, part.result);
            if (png) {
              const cid = `visual_${seq += 1}`;
              images.push({ cid, base64: png.base64 });
              blocks.push(assistantContentBlock(imageHtml(cid, png.width, part.result?.title)));
            }
          } catch {
            blocks.push(assistantContentBlock(
              `<div style="font-size:12px;color:${C.textSecondary};">[visualization could not be exported]</div>`,
            ));
          }
        }
      }
      // Other parts (propose_report_edit / add_visual_component / generate_presentation /
      // raw MCP activity) are not meaningful in an email — skip them.
    }
  }

  return { bodyHtml: wrapDocument(blocks.join('\n')), images };
}

// ---------------------------------------------------------------------------
// MIME .eml assembly + download
// ---------------------------------------------------------------------------

function b64Utf8(str) {
  return window.btoa(unescape(encodeURIComponent(str)));
}

function wrap76(b64) {
  return b64.replace(/.{1,76}/g, '$&\r\n').trimEnd();
}

// RFC 2047 encode a header value only when it contains non-ASCII.
function encodeHeaderValue(value) {
  const v = String(value || '').replace(/[\r\n]+/g, ' ').trim();
  // eslint-disable-next-line no-control-regex
  if (/^[\x00-\x7F]*$/.test(v)) return v;
  return `=?utf-8?B?${b64Utf8(v)}?=`;
}

function assembleEml({ subject, bodyHtml, images }) {
  const boundary = `=_rsv_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  const CRLF = '\r\n';
  const parts = [
    'X-Unsent: 1',
    `Subject: ${encodeHeaderValue(subject)}`,
    'To: ',
    'MIME-Version: 1.0',
    `Content-Type: multipart/related; boundary="${boundary}"`,
    '',
    'This is a multi-part message in MIME format.',
    '',
    `--${boundary}`,
    'Content-Type: text/html; charset=utf-8',
    'Content-Transfer-Encoding: base64',
    '',
    wrap76(b64Utf8(bodyHtml)),
  ];
  for (const img of images) {
    parts.push(
      `--${boundary}`,
      'Content-Type: image/png',
      'Content-Transfer-Encoding: base64',
      `Content-ID: <${img.cid}>`,
      `Content-Disposition: inline; filename="${img.cid}.png"`,
      '',
      wrap76(img.base64),
    );
  }
  parts.push(`--${boundary}--`, '');
  return parts.join(CRLF);
}

function downloadEml(filename, eml) {
  const blob = new Blob([eml], { type: 'message/rfc822' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// ---------------------------------------------------------------------------
// Helpers + public API
// ---------------------------------------------------------------------------

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function firstUserText(messages) {
  const first = (messages || []).find((m) => m.role === 'user');
  return first ? first.parts.filter((p) => p.type === 'text').map((p) => p.text).join('').trim() : '';
}

function filenameFor(subject) {
  const base = String(subject || 'catalyst-chat')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50) || 'catalyst-chat';
  return `${base}.eml`;
}

// Export an entire conversation. Resolves once the .eml download is triggered.
export async function exportConversationToEml(messages, { subject } = {}) {
  const list = Array.isArray(messages) ? messages : [];
  const subj = (subject || firstUserText(list) || 'Catalyst conversation').slice(0, 150);
  const { bodyHtml, images } = await buildEmailBody(list);
  downloadEml(filenameFor(subj), assembleEml({ subject: subj, bodyHtml, images }));
}

// Export a single display-tool result (chart/table/visual). `part` is the tool
// part from a chat message ({ toolName, state:'result', result }).
export async function exportResultToEml(part, { subject } = {}) {
  const messages = [{ role: 'assistant', parts: [part] }];
  const subj = (subject || part?.result?.title || 'Catalyst result').slice(0, 150);
  const { bodyHtml, images } = await buildEmailBody(messages);
  downloadEml(filenameFor(subj), assembleEml({ subject: subj, bodyHtml, images }));
}
